import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, mergeMap } from 'rxjs/operators';
import { TipoContratoService } from '../services/tipo-contrato.service';
import * as TipoContratoActions from './tipo-contrato.actions';
import * as TipoContratoSelectors from './tipo-contrato.selectors';
import { NotificationService } from '../../../shared/services/notification.service';
import { LoggerService } from '../../../core/services/logger.service';

@Injectable()
export class TipoContratoEffects {
  private readonly actions$ = inject(Actions);
  private readonly tipoContratoService = inject(TipoContratoService);
  private readonly store = inject(Store);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);

  // Load Tipos Contrato
  loadTiposContrato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipoContratoActions.loadTiposContrato),
      withLatestFrom(this.store.select(TipoContratoSelectors.selectAllTiposContrato)),
      mergeMap(([{ force }, current]) => {
        if (!force && current && current.length > 0) {
          // Already have data; skip network call
          return of(
            TipoContratoActions.loadTiposContratoSuccess({
              tiposContrato: current
            })
          );
        }

        return this.tipoContratoService.getTiposContrato().pipe(
          map(tiposContrato => TipoContratoActions.loadTiposContratoSuccess({ tiposContrato })),
          catchError(error => {
            this.logger.error('Error loading tipos contrato', error);
            this.notificationService.showError('Error al cargar los tipos de contrato.');
            return of(
              TipoContratoActions.loadTiposContratoFailure({
                error: error.message || 'Error loading tipos contrato'
              })
            );
          })
        );
      })
    )
  );

  // Create Tipo Contrato
  createTipoContrato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipoContratoActions.createTipoContrato),
      mergeMap(({ tipoContrato }) =>
        this.tipoContratoService.createTipoContrato(tipoContrato).pipe(
          mergeMap(createdTipoContrato => {
            this.notificationService.showSuccess('Tipo de contrato creado exitosamente.');
            return of(
              TipoContratoActions.createTipoContratoSuccess({ tipoContrato: createdTipoContrato }),
              TipoContratoActions.loadTiposContrato({ force: true })
            );
          }),
          catchError(error => {
            this.logger.error('Error creating tipo contrato', error);
            this.notificationService.showError('Error al crear el tipo de contrato.');
            return of(
              TipoContratoActions.createTipoContratoFailure({
                error: error.message || 'Error creating tipo contrato'
              })
            );
          })
        )
      )
    )
  );

  // Update Tipo Contrato
  updateTipoContrato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipoContratoActions.updateTipoContrato),
      withLatestFrom(this.store.select(TipoContratoSelectors.selectAllTiposContrato)),
      mergeMap(([{ id, tipoContrato }]) => {
        return this.tipoContratoService.updateTipoContrato(id, tipoContrato).pipe(
          mergeMap(updatedTipoContrato => {
            this.notificationService.showSuccess('Tipo de contrato actualizado exitosamente.');
            return of(
              TipoContratoActions.updateTipoContratoSuccess({
                tipoContrato: updatedTipoContrato
              }),
              TipoContratoActions.loadTiposContrato({ force: true })
            );
          }),
          catchError(error => {
            this.logger.error('Error updating tipo contrato', error);
            this.notificationService.showError('Error al actualizar el tipo de contrato.');
            return of(
              TipoContratoActions.updateTipoContratoFailure({
                error: error.message || 'Error updating tipo contrato'
              })
            );
          })
        );
      })
    )
  );

  // Delete Tipo Contrato
  deleteTipoContrato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipoContratoActions.deleteTipoContrato),
      switchMap(({ id }) =>
        this.tipoContratoService.deleteTipoContrato(id).pipe(
          mergeMap(() => {
            this.notificationService.showSuccess('Tipo de contrato eliminado exitosamente.');
            return of(
              TipoContratoActions.deleteTipoContratoSuccess({ id }),
              TipoContratoActions.loadTiposContrato({ force: true })
            );
          }),
          catchError(error => {
            this.logger.error('Error deleting tipo contrato', error);
            this.notificationService.showError('Error al eliminar el tipo de contrato.');
            return of(
              TipoContratoActions.deleteTipoContratoFailure({
                error: error.message || 'Error deleting tipo contrato'
              })
            );
          })
        )
      )
    )
  );

  // Search Tipos Contrato
  searchTiposContrato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipoContratoActions.searchTiposContrato),
      withLatestFrom(this.store.select(TipoContratoSelectors.selectAllTiposContrato)),
      switchMap(([{ query }, tiposContrato]) => {
        if (!query.trim()) {
          return of(TipoContratoActions.searchTiposContratoSuccess({ results: [] }));
        }

        const results = tiposContrato.filter(tc =>
          tc.nombre.toLowerCase().includes(query.toLowerCase())
        );

        return of(TipoContratoActions.searchTiposContratoSuccess({ results }));
      })
    )
  );
}
